import { Accordion } from "@/components/ui/accordion";
import { SupportedLocale } from "@/i18n";
import {
  computeFieldRelatedAuctions,
  computeFieldRelatedBibliographiesMultiplesamples,
  computeFieldRelatedExhibitions,
} from "@/lib/computeFields";
import { useAuth } from "@/context/auth/AuthContext";
import { filterPublishedMultiplesampleAuctions } from "@/lib/multiplesample-auctions";
import { Multipleprint, Multiplesample } from "@/lib/types";
import { HistoryAccordion } from "@/pages/dashboard/artwork-overview/accordions/HistoryAccordion";
import { useMemo } from "react";
import { AuctionsAccordion } from "./AuctionsAccordion";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { buildMultiplesampleProvenanceHistory } from "./provenanceTimeline";
import { RelatedWorksAccordion } from "./RelatedWorksAccordion";

interface ExemplaireAccordionsProps {
  data: Multipleprint;
  selectedSample: Multiplesample;
  locale: SupportedLocale;
  t: (key: string, options?: Record<string, unknown>) => string;
}

interface SelectedSampleRelations extends Multiplesample {
  multipleSampleInExhibitions?: Array<{
    exhibition?: { expositionsDescriptionResume?: string };
    multiplesampleDansExpositionsDescriptionNumeroCatalogue?: string;
    multiplesampleDansExpositionsNotes?: string;
  }>;
  multipleSampleInBibliography?: Array<{
    bibliography?: { bibliographieDescriptionResumeLong?: string };
    multiplesampleDansBibliographieDescriptionCitePage?: string;
    multiplesampleDansBibliographieDescriptionReproduitPage?: string;
    multiplesampleDansBibliographieDescriptionReproduitPlanche?: string;
    multiplesampleDansBibliographieDescriptionPlancheNonNumerotee?: boolean;
    multiplesampleDansBibliographieDescriptionListePage?: string;
    multiplesampleDansBibliographieDescriptionSousNumero?: string;
    multiplesampleDansBibliographieDescriptionPhotographe?: string;
    multiplesampleDansBibliographieDescriptionReproductionCouleur?: boolean;
    multiSampleInBiblioDescReproCover?: boolean;
    multiplesampleDansBibliographieDescriptionNonPagine?: boolean;
    multiplesampleDansBibliographieDescriptionTexteLibre?: string;
  }>;
  multipleSampleInAuction?: Array<{
    auction?: any;
    multiplesampleDansVentesDescriptionNumeroLot?: string;
    multiplesampleDansVentesDescriptionReproduitPage?: string;
    multiplesampleDansVentesDescriptionNonReproduit?: boolean;
  }>;
  history?: Array<{
    id?: string;
    id_imported?: number;
    provenanceRank?: number;
    exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart?: boolean;
    exemplaireDansEstampesMultiplesHistoryDescriptionNom?: string;
    exemplaireDansEstampesMultiplesHistoryDescriptionVille?: string;
    exemplaireDansEstampesMultiplesHistoryDescriptionDates?: string;
  }>;
}

export function ExemplaireAccordions({
  data,
  selectedSample,
  locale,
  t,
}: ExemplaireAccordionsProps) {
  const { instanceData } = useAuth();
  const provenanceMode = instanceData?.provenanceMode ?? false;
  const sample = selectedSample as SelectedSampleRelations;
  const publishedSampleAuctions = useMemo(
    () => filterPublishedMultiplesampleAuctions(sample.multipleSampleInAuction),
    [sample.multipleSampleInAuction],
  );
  const historyCalculated = useMemo(() => {
    return buildMultiplesampleProvenanceHistory({
      history: sample.history,
      auctions: provenanceMode ? publishedSampleAuctions : [],
      t,
    });
  }, [provenanceMode, publishedSampleAuctions, sample.history, t]);

  const exhibitionsCalculated = useMemo(() => {
    const items = sample.multipleSampleInExhibitions ?? [];
    if (items.length === 0) return "";
    const adapted = items.filter((m) => !!m.exhibition).map((m) => ({
      exhibition: m.exhibition!,
      oeuvresDansExpositionsDescriptionNumeroCatalogue:
        m.multiplesampleDansExpositionsDescriptionNumeroCatalogue ?? "",
      oeuvresDansExpositionsNotes: m.multiplesampleDansExpositionsNotes ?? "",
    }));
    return computeFieldRelatedExhibitions(
      {
        relatedExhbitions: adapted as unknown as Parameters<
          typeof computeFieldRelatedExhibitions
        >[0]["relatedExhbitions"],
      },
      { language: locale, translation: t },
    );
  }, [locale, sample.multipleSampleInExhibitions, t]);

  const bibliographyCalculated = useMemo(() => {
    const items = sample.multipleSampleInBibliography ?? [];
    if (items.length === 0) return "";
    const adapted = items.filter((m) => !!m.bibliography).map((m) => ({
      bibliography: m.bibliography!,
      multiplesampleDansBibliographieDescriptionCitePage:
        m.multiplesampleDansBibliographieDescriptionCitePage ?? "",
      multiplesampleDansBibliographieDescriptionReproduitPage:
        m.multiplesampleDansBibliographieDescriptionReproduitPage ?? "",
      multiplesampleDansBibliographieDescriptionReproduitPlanche:
        m.multiplesampleDansBibliographieDescriptionReproduitPlanche ?? "",
      multiplesampleDansBibliographieDescriptionPlancheNonNumerotee:
        m.multiplesampleDansBibliographieDescriptionPlancheNonNumerotee ?? false,
      multiplesampleDansBibliographieDescriptionListePage:
        m.multiplesampleDansBibliographieDescriptionListePage ?? "",
      multiplesampleDansBibliographieDescriptionSousNumero:
        m.multiplesampleDansBibliographieDescriptionSousNumero ?? "",
      multiplesampleDansBibliographieDescriptionPhotographe:
        m.multiplesampleDansBibliographieDescriptionPhotographe ?? "",
      multiplesampleDansBibliographieDescriptionReproductionCouleur:
        m.multiplesampleDansBibliographieDescriptionReproductionCouleur ?? false,
      multiSampleInBiblioDescReproCover:
        m.multiSampleInBiblioDescReproCover ?? false,
      multiplesampleDansBibliographieDescriptionNonPagine:
        m.multiplesampleDansBibliographieDescriptionNonPagine ?? false,
      multiplesampleDansBibliographieDescriptionTexteLibre:
        m.multiplesampleDansBibliographieDescriptionTexteLibre ?? "",
    }));
    return computeFieldRelatedBibliographiesMultiplesamples(
      {
        relatedBibliographies: adapted as Parameters<
          typeof computeFieldRelatedBibliographiesMultiplesamples
        >[0]["relatedBibliographies"],
      },
      { language: locale, translation: t },
    );
  }, [locale, sample.multipleSampleInBibliography, t]);

  const auctionCalculated = useMemo(() => {
    if (publishedSampleAuctions.length > 0) {
      const sampleItems = publishedSampleAuctions;
      return computeFieldRelatedAuctions(
        {
          relatedAuctions: sampleItems
            .filter((m) => !!m.auction)
            .map((m) => ({
              auction: m.auction,
              oeuvresDansVentesDescriptionNumeroLot:
                m.multiplesampleDansVentesDescriptionNumeroLot ?? "",
              oeuvresDansVentesDescriptionReproduitPage:
                m.multiplesampleDansVentesDescriptionReproduitPage ?? "",
              oeuvresDansVentesDescriptionNonReproduit:
                m.multiplesampleDansVentesDescriptionNonReproduit ?? false,
            })),
        },
        { language: locale, translation: t },
      );
    }

    const parentItems =
      data.multipleprintInAuction?.filter(
        (item) => item.auction?.ventesPublicationStatus === "published",
      ) ?? [];
    if (parentItems.length === 0) return "";
    return computeFieldRelatedAuctions(
      {
        relatedAuctions: parentItems.map((item) => ({
          auction: item.auction,
          oeuvresDansVentesDescriptionNumeroLot:
            item.ventesDansEstampesMultiplesDescriptionNumeroLot,
          oeuvresDansVentesDescriptionReproduitPage:
            item.ventesDansEstampesMultiplesDescriptionReproduitPage,
          oeuvresDansVentesDescriptionNonReproduit: false,
        })),
      },
      { language: locale, translation: t },
    );
  }, [data.multipleprintInAuction, locale, publishedSampleAuctions, t]);

  return (
    <Accordion
      type="multiple"
      className="font-normal text-base text-gray-800 mb-32"
    >
      <HistoryAccordion
        historyCalculated={historyCalculated}
        tirageHistoryCalculated={null}
        t={t}
        title={
          provenanceMode
            ? t("exemplaireTabProvenance")
            : t("exemplaireTabHistorique")
        }
      />
      <ExhibitionsAccordion
        exhibitionsCalculated={exhibitionsCalculated}
        t={t}
      />
      <BibliographyAccordion
        bibliographyCalculated={bibliographyCalculated}
        t={t}
      />
      {!provenanceMode && (
        <AuctionsAccordion auctionCalculated={auctionCalculated} t={t} />
      )}
      <RelatedWorksAccordion data={data} locale={locale} t={t} />
    </Accordion>
  );
}
